import type { FirmPreferences } from '@/_types/models/firm';

export const EMPTY_TAG_STRING = '(no tags)' as const;

// Defaults for reminder frequencies (in days)
export const DEFAULT_QUESTIONNAIRE_REMINDER_FREQUENCY = 7;
export const DEFAULT_DELIVER_REMINDER_FREQUENCY = 3;

export const DEFAULT_FIRM_PREFERENCES: FirmPreferences = {
  _tmp_new_delivery_enabled: false,
  all_activities_enabled: false,
  ai_response_enabled: false,
  annotation_enabled: false,
  append_description_to_filename_enabled: false,
  auto_export_docs: [],
  auto_export_enabled: false,
  billing_enabled: false,
  binders_enabled: false,
  bulk_upload_processor: false,
  client_list_import_diy_enabled: false,
  communication_disabled: false,
  custom_messages_tile_name_for_clients: 'Messages',
  customElements: undefined,
  custom_element_enabled: false,
  custom_leadsheet: undefined,
  custom_statuses_enabled: false,
  disable_file_edit_action: false,
  disable_folder_action: false,
  disable_provided_elsewhere: false,
  document_drop_off_enabled: false,
  drop_link_enabled: false,
  email_assistant_enabled: false,
  export_to_platforms: ['Third-party DMS'],
  export_to_third_party_dms_enabled: false,
  external_entity_id_allowed: false,
  firm_glossary_enabled: false,
  first_section_required: false,
  folders_where_client_actions_disabled: false,
  force_2fa_on_firm_users: false,
  force_2fa_on_users: false,
  has_firm_groups: false,
  hide_document_uploads_within_sections: false,
  hide_documents_view_from_clients: false,
  hide_sign_in_button_on_drop_link: false,
  hide_signature_print_option: false,
  in_app_banner_enabled: false,
  in_app_banner_text: '',
  invoice_reminder_frequency: DEFAULT_DELIVER_REMINDER_FREQUENCY,
  message_tags_disabled: false,
  new_react_app_enabled: true,
  new_tax_return_delivery_enabled: false,
  override_questionnaire_recipient_by_role_enabled: false,
  parent_entity_relevant_questionnaire_statuses_enabled: false,
  payment_voucher_reminder_frequency: DEFAULT_DELIVER_REMINDER_FREQUENCY,
  questionnaire_reminder_frequency: DEFAULT_QUESTIONNAIRE_REMINDER_FREQUENCY,
  remove_please_upload_for_rollover_items: false,
  require_one_signature_per_section: false,
  review_and_export_enabled: false,
  scan_and_auto_flow_export_enabled: false,
  duplicate_document_validation_enabled: false,
  show_external_user_id_in_entities_table: false,
  show_questionnaire_usage_alert: false,
  signature_reminder_frequency: DEFAULT_DELIVER_REMINDER_FREQUENCY,
  signature_requests_enabled: false,
  support_email: 'hello@soraban.com',
  trengo_widget_key: undefined,
  template_disabled_for_nonadmin: false,
  use_firm_roles: false,
  user_dashboard_enabled: false,
};

export const FIRM_STATUS_CHANGE_EMAIL_SUBJECT: string =
  'Update regarding {questionnaire_name} for {questionnaire_year}:';
export const FIRM_STATUS_CHANGE_EMAIL_MESSAGE: string =
  'Hi {client_fname}!\n\nThere has been an new status regarding the {questionnaire_year} {questionnaire_name}:';
export const FIRM_STATUS_CHANGE_EMAIL_MESSAGE_FOOTER: string =
  'Click the below button to view the update.';
export const FIRM_STATUS_CHANGE_SMS_MESSAGE: string =
  '{firm_name}: Hi {client_fname}, there has been an update regarding the {questionnaire_year} {questionnaire_name}:';

// Permission Level Mappings
export const PERMISSION_LEVELS = {
  basic: 'Basic',
  full: 'Staff',
  group: 'Group',
  partner: 'Admin',
} as const;

// Backend access level type
export type BackendAccessLevel = keyof typeof PERMISSION_LEVELS;

// Map UI access levels to backend permission level names (for legacy compatibility)
export const BACKEND_NAME_MAP: Record<string, string> = {
  basic: 'basic',
  full: 'full',
  admin: 'partner', // UI "admin" maps to backend "partner"
  group: 'group',
} as const;

// UI Access Level Keys (for consistency)
export const UI_ACCESS_LEVELS = {
  BASIC: 'basic',
  FULL: 'full',
  ADMIN: 'admin', // This is the UI key, maps to backend 'partner'
  GROUP: 'group',
} as const;

// Permission Level Display Names
export const getPermissionLevelDisplayName = (backendName: string): string => {
  return (
    PERMISSION_LEVELS[backendName as keyof typeof PERMISSION_LEVELS] ||
    backendName
  );
};

// Firm Basic Settings - preferences
export const DEFAULT_FALLBACK_EMAIL = 'noreply@withsoraban.com';
export const DEFAULT_SENDER_ID: number = -1;
